import { Link, NavLink, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import Dashboard from './pages/Dashboard';
import CreateApp from './pages/CreateApp';
import AppDetail from './pages/AppDetail';
import Login from './pages/Login';
import { useAuth } from './lib/auth';

/** Cloupak 几何 Logo 标记（立方体线条，呼应原型的几何装饰风格） */
function LogoMark() {
  return (
    <span className="logo-mark" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2.5} strokeLinecap="round">
        <path d="M12 3L4 7v10l8 4 8-4V7l-8-4z" />
        <path d="M12 3v18M4 7l8 4 8-4" />
      </svg>
    </span>
  );
}

/** 将 plan 字段转为中文标签 */
function planLabel(plan: string): string {
  switch (plan) {
    case 'pro':
      return '专业版';
    case 'enterprise':
      return '企业版';
    default:
      return '免费版';
  }
}

/** 取用户名首字符（大写）作为头像占位文字 */
function avatarText(name: string): string {
  return name.trim().charAt(0).toUpperCase() || 'U';
}

/** 路由守卫：未登录重定向到 /login，校验中显示加载态 */
function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (!user && loading) {
    return (
      <div className="page-wrap">
        <div className="state-box">加载中…</div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

export default function App() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `nav-link${isActive ? ' active' : ''}`;

  function handleLogout() {
    logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="topbar-inner">
          <Link to="/" className="brand" aria-label="Cloupak 首页">
            <LogoMark />
            <span className="brand-name">Cloupak</span>
          </Link>

          <nav className="nav" aria-label="主导航">
            <NavLink to="/" end className={navLinkClass}>
              我的应用
            </NavLink>
            <NavLink to="/create" className={navLinkClass}>
              创建应用
            </NavLink>
          </nav>

          <div className="topbar-right">
            {user ? (
              <>
                <span className="plan-pill">{planLabel(user.plan)}</span>
                <span className="avatar" title={user.name}>
                  {avatarText(user.name)}
                </span>
                <button className="btn btn-ghost btn-sm" onClick={handleLogout}>
                  退出
                </button>
              </>
            ) : (
              <Link to="/login" className="btn btn-primary btn-sm">
                登录
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="content">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/create"
            element={
              <ProtectedRoute>
                <CreateApp />
              </ProtectedRoute>
            }
          />
          <Route
            path="/apps/:id"
            element={
              <ProtectedRoute>
                <AppDetail />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      <footer className="footer">
        <span>Cloupak · 一键上云</span>
        <span className="footer-dot">·</span>
        <span>© 2026 Cloupak</span>
      </footer>
    </div>
  );
}

function NotFound() {
  return (
    <div className="page-wrap">
      <div className="empty-state">
        <div className="empty-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#6c6c7d" strokeWidth={1.5} strokeLinecap="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4M12 16v.01" />
          </svg>
        </div>
        <h2>页面走丢了</h2>
        <p>你访问的页面不存在或已被移动。</p>
        <Link to="/" className="btn btn-primary">
          返回应用列表
        </Link>
      </div>
    </div>
  );
}
